import { PaylinkCustomerInfo, PaylinkPaymentMethodType, PaylinkStatus } from 'skipify-types';
import { z } from 'zod';

type CreatePaylinkItem = {
  sku: string;
  quantity?: number;
  price: number;
  tax?: number;
  description?: string;
  category?: string;
  metadata?: Record<string, unknown>;
};

export type CreatePaylinkBody = {
  merchantId: string;
  lineItems: CreatePaylinkItem[];
  customer: Partial<PaylinkCustomerInfo> & { customerId?: string };
  sendSms: boolean;
  sendEmail: boolean;
  description?: string;
  expiration?: string;
  customerConsent: boolean;
  merchantInvoiceId?: string;
  enableRecurring?: boolean;
  recurringChargeConsent?: boolean;
  enablePartialPayment?: boolean;
};

export type SendPaylinkBody = {
  sendSms: boolean;
  sendEmail: boolean;
};

export type UpdatePaylinkBody = {
  description?: string;
  expiration?: string | null;
};

export type PaginatedResponse<T> = {
  data: T[];
  metadata: {
    totalRecords: number;
    totalPages: number;
    pageNumber: number;
    pageSize: number;
    links: {
      previousPage: string | null;
      nextPage: string | null;
      firstPage: string;
      lastPage: string;
    };
  };
};

export const paylinkCustomerSchema = z.object({
  payLinkCustomerId: z.string(),
  accountName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  networkType: z.string().nullable().optional(),
  lastFour: z.string().nullable().optional(),
  cardArtUrl: z.string().nullable().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PaylinkCustomerInfoResponse = z.infer<typeof paylinkCustomerSchema>;

export type GetAllPaylinksQueryFilters = {
  status?: PaylinkStatus[] | null;
  paymentMethodType?: PaylinkPaymentMethodType[] | null;
  isFlexPay?: boolean | null;
  createdBefore?: string | null;
  createdAfter?: string | null;
  transactedBefore?: string | null;
  transactedAfter?: string | null;
  amountGTE?: number | null;
  amountLTE?: number | null;
  customerIds?: string[] | null;
  enableRecurring?: boolean | null;
};

export const orderByValues = ['asc', 'desc'] as const;
export type OrderByValue = (typeof orderByValues)[number];
export type SortQuery<T = string> = {
  sortBy?: T | null;
  orderBy?: OrderByValue | null;
};

export type GetAllPaylinksSortQuery = SortQuery<'lastSent' | 'deliveryLogDate' | 'createdAt'>;

export type GetAllPaylinksQuery = GetAllPaylinksQueryFilters &
  GetAllPaylinksSortQuery & {
    merchantId: string;
    search?: string;
    pageSize?: number;
    pageNumber?: number;
  };

export type RefundPaylinkTransactionBody = {
  merchantId: string;
  paylinkId: string;
  orderId: string;
  amount?: number;
  transactionId?: string;
};

/** POST /download filter shape (differs from GET list query param names). */
export type DownloadPaylinksBody = {
  merchantId: string;
  createdBefore?: string;
  createdAfter?: string;
  transactedBefore?: string;
  transactedAfter?: string;
  amountFrom?: number;
  amountTo?: number;
  customers?: string[];
  paylinkStatus?: PaylinkStatus[];
  paymentMethodType?: PaylinkPaymentMethodType[];
  isFlexPay?: boolean;
};
